/**
 * Modulator — animates one knob of an effect layer over time so scenes don't
 * sit static: a fog band that swells and recedes, embers that surge, lights that
 * pulse. A layer carries `modulators[]`; the AtmosphereLayer ticks each one every
 * frame: opacity drives the layer's display container (container.alpha), while
 * density/speed push into the layer handle's emitter setters (setDensity/
 * setSpeed). Plus the scene-mood multiplier (game code drives it for day-night /
 * combat / events). Pure math here — TDD'd; the apply-loop lives in AtmosphereLayer.
 */
import { seededRand } from '../../game/effects/atmosphere/atmospherePlacement';

/** What a modulator drives on its layer's handle. Each has a live driver in
 *  AtmosphereLayer's modulation tick (opacity→container.alpha, density→setDensity,
 *  speed→setSpeed, tint→setTint). Don't add a target without wiring its driver.
 *  NOTE tint differs: its value is a 0..1 LERP factor (layer base colour →
 *  `layer.tintTo`), not a multiplier — read via `modulatedTintValue`, not the
 *  multiplying `modulatedValue` (whose no-driver default of 1 is wrong for tint). */
export type ModTarget = 'opacity' | 'density' | 'speed' | 'tint';
/** The waveform between `min` (trough) and `max` (peak). */
export type ModShape = 'sine' | 'pulse' | 'ramp' | 'noise';

export interface Modulator {
  target: ModTarget;
  shape: ModShape;
  periodMs: number;
  min: number;
  max: number;
  /** Cycle offset in [0,1) so layers/anchors don't all peak together. */
  phase?: number;
}

/**
 * The modulator's value at `elapsedMs` — `min`..`max` per the shape. sine starts
 * at min (cosine-eased to max at half-period); pulse is a square min/max; ramp is
 * a min→max sawtooth; noise is a seeded smooth random walk.
 */
export function modulatorValue(mod: Modulator, elapsedMs: number, seed = 0): number {
  const period = Math.max(1, mod.periodMs);
  const phase = (((elapsedMs / period) + (mod.phase ?? 0)) % 1 + 1) % 1; // 0..1
  let t: number; // lerp factor 0..1
  switch (mod.shape) {
    case 'sine':
      t = 0.5 - 0.5 * Math.cos(phase * Math.PI * 2);
      break;
    case 'pulse':
      t = phase < 0.5 ? 1 : 0;
      break;
    case 'ramp':
      t = phase;
      break;
    case 'noise': {
      const steps = 8;
      const i = Math.floor(phase * steps);
      const a = seededRand(seed + i);
      const b = seededRand(seed + i + 1);
      t = a + (b - a) * (phase * steps - i); // lerp between per-step seeded values
      break;
    }
    default:
      t = 0.5;
  }
  return mod.min + (mod.max - mod.min) * t;
}

/** Combine all modulators that drive a given target into one multiplier/value.
 *  Targets multiply (so several can stack); returns 1 when none drive it. */
export function modulatedValue(mods: Modulator[] | undefined, target: ModTarget, elapsedMs: number, seed = 0): number {
  if (!mods || mods.length === 0) return 1;
  let v = 1;
  let found = false;
  for (let i = 0; i < mods.length; i += 1) {
    if (mods[i].target !== target) continue;
    v *= modulatorValue(mods[i], elapsedMs, seed + i);
    found = true;
  }
  return found ? v : 1;
}

/** The 0..1 tint LERP factor for `elapsedMs` from the FIRST `tint` modulator, or
 *  `null` when none drives tint. Unlike `modulatedValue`, absence means "no tint
 *  shift" (not the multiplier default of 1) — so the apply-loop can skip setTint
 *  entirely and leave the layer its authored colour. */
export function modulatedTintValue(mods: Modulator[] | undefined, elapsedMs: number, seed = 0): number | null {
  if (!mods) return null;
  for (let i = 0; i < mods.length; i += 1) {
    if (mods[i].target === 'tint') return modulatorValue(mods[i], elapsedMs, seed + i);
  }
  return null;
}
